import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { UseFormRegister, FieldErrors, Control } from "react-hook-form";
import { Controller } from "react-hook-form";
import { InformationCircleIcon } from "@heroicons/react/outline";

export interface AgentFormData {
  name: string;
  description: string;
  system_prompt: string;
  llm_model: string;
  temperature: number;
  icon: string;
  figure_vision_enabled: boolean;
}

interface AgentConfigFieldsProps {
  register: UseFormRegister<AgentFormData>;
  errors: FieldErrors<AgentFormData>;
  control: Control<AgentFormData>;
}

const LLM_MODELS = [
  { value: "mistral-large-latest", label: "Mistral Large" },
  { value: "mistral-medium-latest", label: "Mistral Medium" },
  { value: "mistral-small-latest", label: "Mistral Small" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini" },
  { value: "gpt-4o", label: "GPT-4o" },
];

const ICONS = [
  { value: "default", emoji: "\uD83E\uDD16", label: "Robot" },
  { value: "book", emoji: "\uD83D\uDCDA", label: "Book" },
  { value: "support", emoji: "\uD83C\uDFA7", label: "Support" },
  { value: "brain", emoji: "\uD83E\uDDE0", label: "Brain" },
  { value: "search", emoji: "\uD83D\uDD0D", label: "Search" },
  { value: "chat", emoji: "\uD83D\uDCAC", label: "Chat" },
  { value: "star", emoji: "\u2B50", label: "Star" },
  { value: "tool", emoji: "\uD83D\uDD27", label: "Tool" },
  { value: "idea", emoji: "\uD83D\uDCA1", label: "Conseil" },
  { value: "language", emoji: "\uD83C\uDF0D", label: "Langues" },
  { value: "education", emoji: "\uD83C\uDF93", label: "Formation" },
  { value: "expert", emoji: "\uD83D\uDC54", label: "Expert" },
];

const InfoTooltip = ({ text }: { text: string }) => (
  <span className="relative inline-block ml-1.5 group/info align-middle">
    <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" strokeWidth={2} />
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/info:opacity-100 transition-opacity pointer-events-none z-10 w-56 text-center whitespace-pre-line font-normal">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </div>
  </span>
);

const FieldLabel = ({ label, hint }: { label: string; hint?: string }) => (
  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
    {label}
    {hint && <InfoTooltip text={hint} />}
  </label>
);

const FieldWrapper = ({ children }: { children: ReactNode }) => <div>{children}</div>;

const AgentConfigFields = ({ register, errors, control }: AgentConfigFieldsProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <FieldWrapper>
        <FieldLabel label={t("general.nameTable")} hint={t("pages.agents.nameHint")} />
        <input
          type="text"
          {...register("name", { required: true })}
          placeholder={t("pages.agents.namePlaceholder")}
        />
        {errors.name && <span className="text-xs text-red-500">Required</span>}
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel
          label={t("pages.agents.description")}
          hint={t("pages.agents.descriptionHint")}
        />
        <input
          type="text"
          {...register("description")}
          placeholder={t("pages.agents.descriptionPlaceholder")}
        />
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel label={t("pages.agents.llmModel")} hint={t("pages.agents.llmModelHint")} />
        <select {...register("llm_model")} style={{ width: "100%" }}>
          {LLM_MODELS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </FieldWrapper>

      <FieldWrapper>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-green-600 shadow-sm focus:ring-green-500"
            {...register("figure_vision_enabled")}
          />
          {t("pages.agents.figureVision")}
          <InfoTooltip text={t("pages.agents.figureVisionHint")} />
        </label>
      </FieldWrapper>

      <FieldWrapper>
        <Controller
          control={control}
          name="temperature"
          render={({ field }) => {
            const percent = ((field.value - 0) / (1 - 0)) * 100;
            return (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <FieldLabel
                    label={t("pages.agents.temperature")}
                    hint={t("pages.agents.temperatureHint")}
                  />
                  <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                    {field.value}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                  aria-label={t("pages.agents.temperature")}
                  style={{
                    accentColor: "rgb(0 180 123)",
                    background: `linear-gradient(to right, rgb(0 180 123) 0%, rgb(0 180 123) ${percent}%, rgb(229 231 235) ${percent}%, rgb(229 231 235) 100%)`,
                    borderRadius: "9999px",
                  }}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0</span>
                  <span>1</span>
                </div>
              </div>
            );
          }}
        />
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel label={t("pages.agents.icon")} hint={t("pages.agents.iconHint")} />
        <Controller
          control={control}
          name="icon"
          render={({ field }) => (
            <div className="grid grid-cols-4 gap-2 max-h-[260px] overflow-y-auto">
              {ICONS.map((i) => (
                <button
                  key={i.value}
                  type="button"
                  onClick={() => field.onChange(i.value)}
                  className={`flex flex-col items-center gap-1 py-3 px-2 rounded-lg border-2 transition-all duration-150 ${
                    field.value === i.value
                      ? "border-green-500 bg-green-50 shadow-sm"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <span className="text-2xl leading-none">{i.emoji}</span>
                  <span
                    className={`text-xs ${
                      field.value === i.value ? "text-green-700 font-medium" : "text-gray-500"
                    }`}
                  >
                    {i.label}
                  </span>
                </button>
              ))}
            </div>
          )}
        />
      </FieldWrapper>
    </div>
  );
};

export default AgentConfigFields;
